/**
 * GET /api/v1/account/users/search — searchable, paginated user lookup.
 *
 * Used instead of `GET /users`, which takes no search term or page and returns
 * the whole account. Also open to every user, not just org admins.
 */
import { http } from '@/lib/http';

/** Backend caps this at 100. */
export const USER_SEARCH_PAGE_SIZE = 50;

/** Per-status totals the search returns alongside the page. */
export interface UserStatusCounts {
  active?: number;
  [status: string]: number | undefined;
}

export interface UserSearchPage {
  users: SearchedUser[];
  /** `users_counts_by_status` — the whole-account totals, not this page's size. */
  counts: UserStatusCounts;
}

export interface SearchedUser {
  id: number;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  avatar_thumb_url?: string | null;
}

export interface FetchUserSearchArgs {
  /** Free-text term. */
  query?: string;
  items?: number;
  /**
   * Asks for users who may be sent on behalf of — delegation enabled and a
   * connected mailbox.
   *
   * The endpoint does not implement it: `filter_by` is permitted against a
   * fixed allowlist (department, office, reporting manager, role, permission,
   * status) that has never carried this key, so it is stripped in transit and
   * every active user comes back. Sent anyway so the filter starts working the
   * day the backend adds it; until then a caller that must not offer
   * non-senders has to narrow the response itself, the way
   * `useSenderAutocomplete` does (ENG-3962).
   */
  emailDelegatableOnly?: boolean;
  signal?: AbortSignal;
}

export async function fetchUserSearch({
  query,
  items = USER_SEARCH_PAGE_SIZE,
  emailDelegatableOnly = false,
  signal,
}: FetchUserSearchArgs = {}): Promise<SearchedUser[]> {
  const trimmed = query?.trim();
  const res = await http.get('account/users/search', {
    params: {
      items,
      page: 1,
      // Without this, deactivated and never-invited users come back too.
      filter_by: {
        status: ['active'],
        ...(emailDelegatableOnly ? { email_delegatable: [true] } : {}),
      },
      ...(trimmed ? { filter_value: trimmed } : {}),
    },
    signal,
  });
  // Interceptor already unwrapped axios, so `res` is the JSON body.
  const users = (res as { data?: { users?: unknown } })?.data?.users;
  return Array.isArray(users) ? (users as SearchedUser[]) : [];
}

/**
 * The same call, keeping `users_counts_by_status` and taking a page.
 *
 * A picker that shows "Users (200)" needs the account total, which one page can
 * never tell it — the response already carries it, `fetchUserSearch` just drops
 * it. Kept as a second function so existing callers keep their array return.
 */
export async function fetchUserSearchPage({
  query,
  items = USER_SEARCH_PAGE_SIZE,
  page = 1,
  signal,
}: FetchUserSearchArgs & { page?: number } = {}): Promise<UserSearchPage> {
  const trimmed = query?.trim();
  const res = await http.get('account/users/search', {
    params: {
      items,
      page,
      filter_by: { status: ['active'] },
      ...(trimmed ? { filter_value: trimmed } : {}),
    },
    signal,
  });
  const body = (
    res as {
      data?: { users?: unknown; users_counts_by_status?: unknown };
    }
  )?.data;
  return {
    users: Array.isArray(body?.users) ? (body.users as SearchedUser[]) : [],
    counts:
      body?.users_counts_by_status &&
      typeof body.users_counts_by_status === 'object'
        ? (body.users_counts_by_status as UserStatusCounts)
        : {},
  };
}

/** Name if we have one, else email — never a blank chip. */
export function searchedUserName(user: SearchedUser): string {
  const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return name || user.email || `User ${user.id}`;
}
